import type { ComponentType } from 'react'
import { createBrowserRouter, type RouteObject } from 'react-router'

import { NotFound, RouteError } from './errors'
import { GuestOnly, RequireRole, RootRedirect } from './guards'

type PageModule = { default: ComponentType }
const page = (load: () => Promise<PageModule>): Pick<RouteObject, 'lazy'> => ({
  lazy: async () => ({ Component: (await load()).default }),
})

export const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      { index: true, element: <RootRedirect /> },

      // -------------------------------------------------------- sign-in
      {
        element: <GuestOnly />,
        children: [
          { path: 'login', ...page(() => import('@/features/auth/StudentLoginPage')) },
          { path: 'staff/login', ...page(() => import('@/features/auth/StaffLoginPage')) },
          { path: 'forgot-password', ...page(() => import('@/features/auth/ForgotPasswordPage')) },
        ],
      },
      { path: 'reset-password', ...page(() => import('@/features/auth/ResetPasswordPage')) },

      // -------------------------------------------------------- staff (admin + centre)
      {
        element: <RequireRole roles={['admin', 'franchisee']} loginPath="/staff/login" />,
        children: [
          {
            path: 'staff',
            ...page(() => import('@/features/staff/StaffShell')),
            children: [
              { index: true, ...page(() => import('@/features/staff/dashboard/DashboardPage')) },
              { path: 'students', ...page(() => import('@/features/staff/students/StudentsPage')) },
              { path: 'students/new', ...page(() => import('@/features/staff/students/StudentFormPage')) },
              { path: 'students/imports', ...page(() => import('@/features/staff/students/ImportsPage')) },
              { path: 'students/:id', ...page(() => import('@/features/staff/students/StudentDetailPage')) },
              {
                path: 'students/:id/edit',
                ...page(() => import('@/features/staff/students/StudentFormPage')),
              },
              {
                path: 'applications',
                ...page(() => import('@/features/staff/applications/ApplicationsPage')),
              },
              {
                path: 'applications/:id',
                ...page(() => import('@/features/staff/applications/ApplicationDetailPage')),
              },
              { path: 'reports', ...page(() => import('@/features/staff/reports/ReportsPage')) },
              { path: 'academics', ...page(() => import('@/features/staff/academics/AcademicsPage')) },
              { path: 'account', ...page(() => import('@/features/staff/account/AccountPage')) },
              {
                element: <RequireRole roles={['admin']} loginPath="/staff/login" />,
                children: [
                  { path: 'jobs', ...page(() => import('@/features/staff/jobs/JobsPage')) },
                  { path: 'jobs/new', ...page(() => import('@/features/staff/jobs/JobFormPage')) },
                  { path: 'jobs/:id', ...page(() => import('@/features/staff/jobs/JobDetailPage')) },
                  { path: 'jobs/:id/edit', ...page(() => import('@/features/staff/jobs/JobFormPage')) },
                  { path: 'employers', ...page(() => import('@/features/staff/employers/EmployersPage')) },
                  {
                    path: 'employers/:id',
                    ...page(() => import('@/features/staff/employers/EmployerDetailPage')),
                  },
                  { path: 'centres', ...page(() => import('@/features/staff/centres/CentresPage')) },
                  { path: 'team', ...page(() => import('@/features/staff/team/TeamPage')) },
                  { path: 'audit', ...page(() => import('@/features/staff/audit/AuditPage')) },
                ],
              },
            ],
          },
        ],
      },

      // -------------------------------------------------------- student portal
      {
        element: <RequireRole roles={['student']} loginPath="/login" />,
        children: [
          { path: 'portal/set-password', ...page(() => import('@/features/auth/SetPasswordPage')) },
          {
            path: 'portal',
            ...page(() => import('@/features/portal/PortalShell')),
            children: [
              { index: true, ...page(() => import('@/features/portal/PortalHomePage')) },
              { path: 'jobs', ...page(() => import('@/features/portal/PortalJobsPage')) },
              { path: 'jobs/:id', ...page(() => import('@/features/portal/PortalJobDetailPage')) },
              { path: 'applications', ...page(() => import('@/features/portal/PortalApplicationsPage')) },
              { path: 'resume', ...page(() => import('@/features/portal/PortalResumePage')) },
              { path: 'resume/builder', ...page(() => import('@/features/portal/PortalResumeBuilderPage')) },
              { path: 'support', ...page(() => import('@/features/portal/PortalSupportPage')) },
            ],
          },
        ],
      },

      { path: '*', element: <NotFound /> },
    ],
  },
])
